/**
 * Reports > Arrival (user, 2026-10-17): the legacy SeedTrac arrival reports, column for column — the
 * Daily Arrival Report (wmsd arrival/report_arrival.php) and the ten "Arrival Report - PM" reports
 * (wmsd reportarrival/arreports.php), read view-only on 2026-10-17. Each returns the shared
 * `ReportResult`, so the screen table, Excel and PDF are the RSW reports' own builders.
 *
 * Legacy "Fresh Seed with PDN" is nexseed's S-Capture and PDN arrivals together (user, 2026-10-17).
 */
import type {
  ReportCellFormat,
  ReportColumn,
  ReportDef,
  ReportFilter,
} from "./warehouse-reports";

/** The arrival kinds a report can be run for, with the nexseed forms behind each. */
export const ARRIVAL_TYPES = {
  fresh: {
    label: "Fresh Seed Arrival",
    forms: ["arrival_scapture", "arrival_pdn"],
  },
  trading: { label: "Trading Arrival", forms: ["arrival_trading"] },
  st: { label: "Stock Transfer-In", forms: ["arrival_stock_transfer"] },
  maize: { label: "Maize-Dried Seed Arrival", forms: ["arrival_cob_receipt"] },
  /** The cob truck itself, booked at the dryer (user, 2026-10-28), not the plant's dried-seed receipt. */
  cob: { label: "Maize Cob Arrival", forms: ["arrival_cob"] },
} as const;
export type ArrivalType = keyof typeof ARRIVAL_TYPES;
export const ARRIVAL_TYPE_KEYS = Object.keys(ARRIVAL_TYPES) as ArrivalType[];

const c = (
  key: string,
  label: string,
  format: ReportCellFormat,
  more: Partial<Omit<ReportColumn, "key" | "label" | "format">> = {}
): ReportColumn => ({
  key,
  label,
  format,
  align:
    format === "int" || format === "qty" || format === "pct"
      ? "right"
      : format === "text"
        ? "left"
        : "center",
  ...more,
});

const SERIAL = c("serial", "#", "int", { align: "center" });
const TXN = c("docNo", "Transaction ID", "text");
const ARRIVED = c("arrivedOn", "Date of Arrival", "date");
const CROP = c("crop", "Crop", "text");
const VARIETY = c("variety", "Variety", "text");
/** The lot's SP codes: F and M under one "SP Code" heading, as the Unloading Sheet heads them (user, 2026-10-26). */
const SPC_F = c("spFemale", "F", "text", { group: "SP Code" });
const SPC_M = c("spMale", "M", "text", { group: "SP Code" });
const LOT = c("lotNumber", "Lot No.", "text");
const NOB = c("bags", "NoB", "int", { total: true });
const QTY = c("qty", "Qty", "qty", { total: true });
const STAGE = c("stage", "Stage", "text", { align: "center" });
const FRN = c("frnNo", "FRN No.", "text");
const PROD_LOC = c("productionLocation", "Prod. Loc.", "text");
const PROD_PERSON = c("productionPerson", "Prod. Personnel", "text");
const ORGANISER = c("organiser", "Organiser", "text");
const FARMER = c("farmer", "Farmer", "text");
const PLOT = c("plotNo", "Plot No.", "text");
const VLOT = c("vendorLotNo", "V. Lot No.", "text");
const QC_STATUS = c("qcStatus", "QC Status", "text", { align: "center" });
const GOT = c("gotStatus", "GOT Status", "text", { align: "center" });
const SEED = c("seedStatus", "Seed Status", "text", { align: "center" });
const DOT = c("testedOn", "DoT", "date");
const DOGR = c("dogr", "DoGR", "date");
const SLOC = c("slocs", "SLOC", "slocs");
const FROM_PLANT = c("sourcePlant", "Stock Transfer From Plant", "text");

/** "Preliminary QC": what the arrival recorded (verdict or percent). */
const PRELIM = [
  c("prelimPp", "PP", "text", { group: "Preliminary QC", align: "center" }),
  c("prelimMoist", "Moist %", "text", {
    group: "Preliminary QC",
    align: "center",
  }),
];
/** "QC": the lot's latest lab result, else what the arrival recorded. */
const QC2 = [
  c("qcPp", "PP", "text", { group: "QC", align: "center" }),
  c("qcMoist", "Moist %", "text", { group: "QC", align: "center" }),
];
const QC3 = [...QC2, c("germinationPct", "Germ %", "pct", { group: "QC" })];

/** Declared, received and the difference, each NoB and Qty, under one heading per pair. */
const diffCols = (declared: string, actual: string) => [
  c("declaredBags", "NoB", "int", { group: declared, total: true }),
  c("declaredQty", "Qty", "qty", { group: declared, total: true }),
  c("receivedBags", "NoB", "int", { group: actual, total: true }),
  c("receivedQty", "Qty", "qty", { group: actual, total: true }),
  c("diffBags", "NoB", "int", { group: "Difference", total: true }),
  c("diffQty", "Qty", "qty", { group: "Difference", total: true }),
];

const TOO_MANY = "Too many lots. Shorten the dates or choose a crop.";

const def = <K extends ArrivalReportKey>(
  key: K,
  title: string,
  filters: readonly ReportFilter[],
  columns: readonly ReportColumn[],
  more: Partial<Pick<ReportDef, "kind" | "note">> = {}
): ReportDef & { key: K } => ({
  key,
  menu: title,
  title,
  asOn: false,
  kind: "flat" as const,
  filters: ["period", ...filters] as readonly ReportFilter[],
  columns,
  tooMany: TOO_MANY,
  ...more,
});

const DAILY_FRESH_COLS = [
  SERIAL,
  ARRIVED,
  TXN,
  CROP,
  VARIETY,
  LOT,
  NOB,
  QTY,
  STAGE,
  PROD_LOC,
  ...PRELIM,
  QC_STATUS,
  GOT,
  SEED,
  SLOC,
];
const ST_QC_COLS = [...QC3, QC_STATUS, DOT, GOT, DOGR];
const PERSON_COLS = [
  SERIAL,
  ARRIVED,
  CROP,
  VARIETY,
  SPC_F,
  SPC_M,
  STAGE,
  FRN,
  LOT,
  NOB,
  QTY,
  ...PRELIM,
  GOT,
];
/** PERSON_COLS with the report's own name column right after the arrival date. */
const withName = (name: ReportColumn) => [
  PERSON_COLS[0]!,
  PERSON_COLS[1]!,
  name,
  ...PERSON_COLS.slice(2),
];

export const ARRIVAL_REPORT_KEYS = [
  "arrival-daily-fresh",
  "arrival-daily-maize",
  "arrival-daily-trading",
  "arrival-daily-st",
  "arrival-daily-cob",
  "arrival-consolidated",
  "arrival-diff-fresh",
  "arrival-diff-trading",
  "arrival-diff-st",
  "arrival-organiser",
  "arrival-location",
  "arrival-personnel",
  "arrival-st-crop",
  "arrival-st-plant",
  "arrival-trading-vendor",
  "arrival-trading-crop",
] as const;
export type ArrivalReportKey = (typeof ARRIVAL_REPORT_KEYS)[number];

export const ARRIVAL_REPORT_DEFS: {
  [K in ArrivalReportKey]: ReportDef & { key: K };
} = {
  "arrival-daily-fresh": def(
    "arrival-daily-fresh",
    "Fresh Seed Arrival",
    ["arrivalType"],
    DAILY_FRESH_COLS
  ),
  "arrival-daily-maize": def(
    "arrival-daily-maize",
    "Maize-Dried Seed Arrival",
    ["arrivalType"],
    DAILY_FRESH_COLS
  ),
  "arrival-daily-trading": def(
    "arrival-daily-trading",
    "Trading Arrival",
    ["arrivalType"],
    [
      SERIAL,
      ARRIVED,
      TXN,
      CROP,
      VARIETY,
      VLOT,
      c("vendor", "Vendor Name", "text"),
      LOT,
      NOB,
      QTY,
      STAGE,
      ...QC2,
      QC_STATUS,
      GOT,
      SEED,
      SLOC,
    ]
  ),
  "arrival-daily-st": def(
    "arrival-daily-st",
    "Stock Transfer-In",
    ["arrivalType"],
    [
      SERIAL,
      ARRIVED,
      FROM_PLANT,
      TXN,
      CROP,
      VARIETY,
      LOT,
      NOB,
      QTY,
      STAGE,
      ...ST_QC_COLS,
      SLOC,
    ]
  ),
  // The truck's note and both weighbridge readings repeat on each of its lots; only lot figures total.
  "arrival-daily-cob": def(
    "arrival-daily-cob",
    "Maize Cob Arrival",
    ["arrivalType"],
    [
      SERIAL,
      ARRIVED,
      TXN,
      c("dryer", "Dryer", "text"),
      c("dcNo", "DC No.", "text"),
      c("dispatchDate", "Dispatch Date", "date"),
      c("vehicleNo", "Lorry No.", "text"),
      c("driver", "Driver", "text"),
      c("loadedKg", "Loaded", "qty", { group: "Truck Weight (kg)" }),
      c("emptyKg", "Empty", "qty", { group: "Truck Weight (kg)" }),
      c("netKg", "Net", "qty", { group: "Truck Weight (kg)" }),
      c("lotNumber", "PDN / Lot No.", "text"),
      SPC_F,
      SPC_M,
      ...diffCols("As per DC", "Actual"),
      c("prelimMoist", "Cob Moist %", "text", { align: "center" }),
      SLOC,
    ]
  ),
  "arrival-consolidated": def(
    "arrival-consolidated",
    "Consolidated Arrival Report",
    ["arrivalType", "crop", "variety"],
    [
      SERIAL,
      CROP,
      VARIETY,
      STAGE,
      c("qty", "Total Qty", "qty", { total: true }),
      c("gotRQty", "Qty: GOT-R", "qty", { total: true }),
      c("gotNrQty", "Qty: GOT-NR", "qty", { total: true }),
    ],
    { kind: "aggregated" }
  ),
  "arrival-diff-fresh": def(
    "arrival-diff-fresh",
    "Material Transit Difference Report",
    ["arrivalType"],
    [
      SERIAL,
      ARRIVED,
      CROP,
      VARIETY,
      LOT,
      ...diffCols("As per PDN", "As per Actuals"),
      PROD_LOC,
      ORGANISER,
      FARMER,
    ]
  ),
  "arrival-diff-trading": def(
    "arrival-diff-trading",
    "Material Transit Difference Report",
    ["arrivalType"],
    [
      SERIAL,
      ARRIVED,
      CROP,
      c("vendor", "Name", "text", { group: "Vendor" }),
      c("vendorVariety", "Variety Name", "text", { group: "Vendor" }),
      c("vendorLotNo", "Vendor Lot No.", "text", { group: "Vendor" }),
      VARIETY,
      LOT,
      ...diffCols("As per DC", "As Actuals"),
    ]
  ),
  "arrival-diff-st": def(
    "arrival-diff-st",
    "Material Transit Difference Report",
    ["arrivalType"],
    [
      SERIAL,
      ARRIVED,
      c("sourcePlant", "Arrival From Plant", "text"),
      CROP,
      VARIETY,
      LOT,
      c("stnNo", "STN No.", "text"),
      ...diffCols("As per STN", "As Actuals"),
    ]
  ),
  "arrival-organiser": def(
    "arrival-organiser",
    "Organiser Wise Arrivals Report",
    ["organiser"],
    // The organiser on every row too, not only above each table (user, 2026-11-05: "the name of
    // the organiser is not visible anywhere").
    [...withName(ORGANISER), PROD_LOC, PROD_PERSON, FARMER, PLOT],
    // One table per organiser, so ALL organisers stay apart (user, 2026-11-03).
    { kind: "sectioned" }
  ),
  "arrival-location": def(
    "arrival-location",
    "Production Location wise Arrivals Report",
    ["state", "prodLocation", "organiser", "crop", "variety"],
    [
      ...PERSON_COLS,
      c("productionPerson", "Production Personnel", "text"),
      ORGANISER,
      FARMER,
      c("state", "State", "text"),
      c("productionLocation", "Production Location", "text"),
    ],
    // One table per production location, as Organiser Wise is (user, 2026-11-03).
    { kind: "sectioned" }
  ),
  "arrival-personnel": def(
    "arrival-personnel",
    "Production Personnel wise Seed Arrival Report",
    ["personnel"],
    [
      // The production personnel on every row too (user, 2026-11-05).
      ...withName(c("productionPerson", "Production Personnel", "text")),
      c("productionLocation", "Production Location", "text"),
      ORGANISER,
      FARMER,
      PLOT,
    ],
    // One table per production personnel, as Organiser Wise is (user, 2026-11-03).
    { kind: "sectioned" }
  ),
  "arrival-st-crop": def(
    "arrival-st-crop",
    "Crop Variety wise Stock Transfer Report",
    ["crop", "variety"],
    [
      SERIAL,
      ARRIVED,
      c("sourcePlant", "Stock Transfer from Plant", "text"),
      VARIETY,
      LOT,
      NOB,
      QTY,
      STAGE,
      ...ST_QC_COLS,
    ]
  ),
  "arrival-st-plant": def(
    "arrival-st-plant",
    "Stock Transfer Plant Wise Report",
    ["sourcePlant"],
    [SERIAL, ARRIVED, CROP, VARIETY, LOT, NOB, QTY, STAGE, ...ST_QC_COLS]
  ),
  "arrival-trading-vendor": def(
    "arrival-trading-vendor",
    "Trading Vendor Wise Report",
    ["vendor"],
    [
      SERIAL,
      ARRIVED,
      CROP,
      VARIETY,
      FRN,
      VLOT,
      LOT,
      NOB,
      QTY,
      STAGE,
      ...PRELIM,
      QC_STATUS,
      GOT,
    ],
    // One table per trading party, as Organiser Wise is (user, 2026-11-03).
    { kind: "sectioned" }
  ),
  "arrival-trading-crop": def(
    "arrival-trading-crop",
    "Crop Variety wise Trading Arrivals Report",
    ["crop", "variety"],
    [
      SERIAL,
      ARRIVED,
      VARIETY,
      c("vendor", "Vendor Name", "text"),
      c("vendorLotNo", "Vendor Lot No.", "text"),
      LOT,
      NOB,
      QTY,
      STAGE,
      ...QC2,
      QC_STATUS,
      GOT,
    ],
    // One Crop | Variety table per variety, as Organiser Wise splits (user, 2026-11-03).
    { kind: "sectioned" }
  ),
};

/** What a Consolidated row lists when clicked: the arrivals summed into it, date by date. */
export const CONSOLIDATED_BREAKDOWN_COLUMNS: readonly ReportColumn[] = [
  SERIAL,
  ARRIVED,
  TXN,
  LOT,
  NOB,
  QTY,
  QC_STATUS,
  GOT,
];

/** The arrival types each report offers; the first is the default. */
export const DAILY_ARRIVAL_TYPES: readonly ArrivalType[] = [
  "fresh",
  "trading",
  "st",
  "maize",
  "cob",
];
export const CONSOLIDATED_ARRIVAL_TYPES: readonly ArrivalType[] = [
  "fresh",
  "trading",
  "st",
  "maize",
];
export const DIFFERENCE_ARRIVAL_TYPES: readonly ArrivalType[] = [
  "fresh",
  "trading",
  "st",
];

/**
 * The ten reports in the legacy menu's order (Daily, then the PM list): the slug in the URL, the
 * menu words, and which definition a run uses (Daily and Difference switch layout by arrival type).
 */
export const ARRIVAL_REPORT_LIST = [
  {
    slug: "daily",
    menu: "Daily/Periodical Arrival Report",
    hint: "Lot by lot for a period, one arrival type at a time, with QC, GOT and SLOC",
    types: DAILY_ARRIVAL_TYPES,
  },
  {
    slug: "consolidated",
    menu: "Consolidated Arrival Report",
    hint: "Qty by crop, variety and stage, split GOT-R / GOT-NR",
    types: CONSOLIDATED_ARRIVAL_TYPES,
  },
  {
    slug: "transit-difference",
    menu: "Material Transit Difference Report",
    hint: "As per PDN / DC / STN against what arrived, lot by lot",
    types: DIFFERENCE_ARRIVAL_TYPES,
  },
  {
    slug: "organiser",
    menu: "Organiser Wise Report",
    hint: "Fresh seed arrivals of one organiser",
    types: [],
  },
  {
    slug: "production-location",
    menu: "Production Location Wise Report",
    hint: "Fresh seed arrivals by state, production location, organiser, crop",
    types: [],
  },
  {
    slug: "production-personnel",
    menu: "Production Personnel Wise Report",
    hint: "Fresh seed arrivals of one production person",
    types: [],
  },
  {
    slug: "st-crop-variety",
    menu: "Stock Transfer Crop Variety Report",
    hint: "Stock transfers received, by crop and variety, with QC and GOT",
    types: [],
  },
  {
    slug: "st-plant",
    menu: "Stock Transfer Plant Wise Report",
    hint: "Stock transfers received from one plant, with QC and GOT",
    types: [],
  },
  {
    slug: "trading-vendor",
    menu: "Trading Vendor Wise Report",
    hint: "Trading arrivals from one party",
    types: [],
  },
  {
    slug: "trading-crop-variety",
    menu: "Trading Arrival Crop Variety Wise Report",
    hint: "Trading arrivals by crop and variety, with vendor lot",
    types: [],
  },
] as const satisfies readonly {
  slug: string;
  menu: string;
  hint: string;
  types: readonly ArrivalType[];
}[];

export type ArrivalReportSlug = (typeof ARRIVAL_REPORT_LIST)[number]["slug"];
export const ARRIVAL_REPORT_SLUGS = ARRIVAL_REPORT_LIST.map((r) => r.slug);

export const isArrivalReportSlug = (s: string): s is ArrivalReportSlug =>
  (ARRIVAL_REPORT_SLUGS as string[]).includes(s);

/** The definition a run of a report prints with. */
export function arrivalReportKey(
  slug: ArrivalReportSlug,
  type: ArrivalType | undefined
): ArrivalReportKey {
  switch (slug) {
    case "daily":
      return `arrival-daily-${type ?? "fresh"}` as ArrivalReportKey;
    case "consolidated":
      return "arrival-consolidated";
    case "transit-difference":
      return `arrival-diff-${type === "trading" || type === "st" ? type : "fresh"}`;
    case "organiser":
      return "arrival-organiser";
    case "production-location":
      return "arrival-location";
    case "production-personnel":
      return "arrival-personnel";
    case "st-crop-variety":
      return "arrival-st-crop";
    case "st-plant":
      return "arrival-st-plant";
    case "trading-vendor":
      return "arrival-trading-vendor";
    case "trading-crop-variety":
      return "arrival-trading-crop";
  }
}
